import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'Company Registration', path: '/services/company-registration' },
    { name: 'GST Registration', path: '/services/gst-registration' },
    { name: 'Startup India Certificate', path: '/services/startup-india' },
    { name: 'ISO Certification', path: '/services/iso-certification' },
    { name: 'Trademark Registration', path: '/services/trademark' },
    { name: 'Business Loans', path: '/services/business-loans' },
  ];

  return (
    <footer className="bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info with Logo */}
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-lg overflow-hidden">
                  <img 
                    src="/company-logo.png" 
                    alt="U" 
                    className="h-10 w-10 object-contain"
                  />
                </div>
                <div>
                  <div className="text-xl font-bold tracking-wide">
                    <span className="text-[#FFB703]">U</span>TKARSH<span className="text-[#FFB703]">UDYAM</span>
                  </div>
                  <div className="text-xs text-gray-400">Solution Private Limited</div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-300 mb-6 leading-relaxed">
              Ahmedabad's Trusted Business & Compliance Partner. Empowering
              Startups, MSMEs, and Growing Businesses.
            </p>
            <div className="flex items-center gap-2 text-[#FFB703]">
              <span className="text-2xl">⭐⭐⭐⭐⭐</span>
              <span className="text-sm font-medium">5.0 Google Rated</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 relative inline-block">
              Quick Links
              <span className="absolute bottom-0 left-0 w-12 h-1 bg-[#FFB703]"></span>
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-300 hover:text-[#FFB703] transition-colors inline-block hover:translate-x-2 duration-300"
                  >
                    → {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Services - Now with Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 relative inline-block">
              Popular Services
              <span className="absolute bottom-0 left-0 w-12 h-1 bg-[#FFB703]"></span>
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.path}
                    className="text-sm text-gray-300 hover:text-[#FFB703] transition-colors inline-block hover:translate-x-2 duration-300"
                  >
                    → {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 relative inline-block">
              Contact Us
              <span className="absolute bottom-0 left-0 w-12 h-1 bg-[#FFB703]"></span>
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-[#FFB703] mt-1 flex-shrink-0" />
                <p className="text-sm text-gray-300 leading-relaxed">
                  A/5, Rajshree Avenue, near Dinesh Hall, Navrangpura, Ahmedabad,
                  Gujarat - 380009
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#FFB703] flex-shrink-0" />
                <div className="text-sm">
                  <a
                    href="tel:9316085383"
                    className="text-gray-300 hover:text-[#FFB703] transition-colors block"
                  >
                    9316085383
                  </a>
                  <a
                    href="tel:9755085383"
                    className="text-gray-300 hover:text-[#FFB703] transition-colors block"
                  >
                    9755085383
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-[#FFB703] flex-shrink-0" />
                <a
                  href="mailto:Neelesh@utkarshudyam.com"
                  className="text-sm text-gray-300 hover:text-[#FFB703] transition-colors"
                >
                  Neelesh@utkarshudyam.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-[#FFB703] flex-shrink-0" />
                <p className="text-sm text-gray-300">Mon - Sat: 9:00 AM - 7:00 PM</p>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="mt-12 pt-8 border-t border-white/20">
          <div className="text-center mb-6">
            <h4 className="text-sm font-semibold mb-4 text-gray-300">
              Government Registered & Certified
            </h4>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-400">
              <span>CIN: U69200MP2024PTC071073</span>
              <span>|</span>
              <span>MSME: UDYAM-MP-23-0142572</span>
              <span>|</span>
              <span>GST: 24AADCU5701A1ZZ</span>
              <span>|</span>
              <span>DPIIT: DIPP172158</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-white/20 text-center">
          <p className="text-sm text-gray-300">
            © {currentYear} Utkarshudyam Solution Private Limited. All Rights
            Reserved.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Designed & Developed with ❤️ for Indian Entrepreneurs
          </p>
        </div>
      </div>
    </footer>
  );
};